import { motion, useSpring, useMotionValue, useTransform } from 'motion/react'
import React, { useEffect } from 'react'

import { SPRING_CONFIG } from 'src/shared/constants/motion'
import { cn } from 'src/shared/utils/cn'

interface ProgressBarProps {
  /** Value between 0 and 100 */
  value: number
  /** Optional color class for the progress bar fill */
  color?: string
  /** Optional height class */
  height?: string
  /** Optional additional className */
  className?: string
  /** Optional delay in seconds before animation starts */
  delay?: number
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  color = 'bg-points',
  height = 'h-2',
  className,
  delay = 0,
}) => {
  // Ensure value is between 0 and 100
  const clampedValue = Math.min(Math.max(value, 0), 100)

  // Use motion value for smooth animation
  const progress = useMotionValue(0)
  const springProgress = useSpring(progress, SPRING_CONFIG)
  const width = useTransform(springProgress, (latest) => `${latest}%`)

  useEffect(() => {
    const timer = setTimeout(() => {
      progress.set(clampedValue)
    }, delay * 1000)
    return () => clearTimeout(timer)
  }, [delay, clampedValue, progress])

  return (
    <div className={cn(height, 'w-full overflow-hidden rounded-full bg-muted/20', className)}>
      <motion.div className={cn('h-full', color)} style={{ width }} />
    </div>
  )
}
